import { c as _c } from "react/compiler-runtime";
import type { MarketParams } from '@morpho-org/blue-sdk';
import { ZERO } from '@morpho-org/dapp-core';
import { useMemo } from 'react';
import { useIsProtectedLltvPending, useProtectedLltv } from '@/components/common/AutoDeleverage/ProtectedLltv';
import { useWeb3Context } from '@/contexts/Web3Context';
import { getBorrowCeilingLtv, getDisplayedMaxLtv } from '../marketMaxLtv';
import { computeMaxBorrowAssets } from './marketMaxAmounts';
import { useMarketBlue } from './useMarketBlue';
export interface UseMarketMaxBorrowV2Params {
  /** Target market params (for the fresh on-chain position via useMarketBlue). */
  marketParams: MarketParams | undefined;
  /** Collateral supplied atomically in the same combo (raises the LTV-bound max); 0 for a plain borrow. */
  proposedCollateral: bigint;
  /** Total reallocatable shared liquidity (from `useMarketReallocations`); raises the liquidity cap (WS6). */
  maxReallocatable: bigint | undefined;
  /** `marketSupplyBorrow` flow flag — gates the position fetch. */
  enabled: boolean;
}

/**
 * Max borrow for the market-actions-v2 path (WS7), the blue-sdk sister of the
 * legacy `simulation-sdk`-sourced `useGetUserMaxMarketBorrow`.
 *
 * Sources the fresh on-chain position from {@link useMarketBlue} (shared with the
 * builder's query cache) and caps the borrow at the tighter of the displayed
 * max-LTV bound (`getDisplayedMaxLtv`, S2) and the available liquidity — on-market
 * plus the public-allocator `maxReallocatable` headroom (WS6), so the displayed max
 * matches what `borrow({ reallocations })` can actually tap and never reverts.
 */
export function useMarketMaxBorrowV2(t0) {
  const $ = _c(23);
  const {
    marketParams,
    proposedCollateral,
    maxReallocatable,
    enabled
  } = t0;
  const {
    account,
    targetChainId
  } = useWeb3Context();
  const {
    positionData,
    isLoading
  } = useMarketBlue(enabled ? marketParams : undefined);
  const t1 = marketParams?.lltv;
  const t2 = marketParams?.id;
  let t3;
  if ($[0] !== t1 || $[1] !== t2 || $[2] !== targetChainId) {
    t3 = {
      lltv: t1,
      marketId: t2,
      chainId: targetChainId
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = targetChainId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const protectedLltv = useProtectedLltv(t3);
  const t4 = marketParams?.id;
  let t5;
  if ($[4] !== t4 || $[5] !== targetChainId) {
    t5 = {
      marketId: t4,
      chainId: targetChainId
    };
    $[4] = t4;
    $[5] = targetChainId;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const isProtectedLltvPending = useIsProtectedLltvPending(t5);
  let t6;
  bb0: {
    if (!enabled || !positionData || !protectedLltv) {
      let t7;
      if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = {
          maxBorrow: undefined,
          maxBorrowCeiling: undefined
        };
        $[7] = t7;
      } else {
        t7 = $[7];
      }
      t6 = t7;
      break bb0;
    }
    const t7 = maxReallocatable ?? ZERO;
    let t8;
    let t9;
    if ($[8] !== positionData.borrowShares || $[9] !== positionData.collateral || $[10] !== positionData.market || $[11] !== proposedCollateral || $[12] !== protectedLltv || $[13] !== t7) {
      const base = {
        market: positionData.market,
        collateral: positionData.collateral,
        borrowShares: positionData.borrowShares,
        proposedCollateral,
        maxReallocatable: t7
      };
      const {
        lltv
      } = positionData.market.params;
      t8 = computeMaxBorrowAssets({
        ...base,
        maxLtv: getDisplayedMaxLtv({
          lltv,
          protectedLltv
        })
      });
      t9 = computeMaxBorrowAssets({
        ...base,
        maxLtv: getBorrowCeilingLtv(protectedLltv)
      });
      $[8] = positionData.borrowShares;
      $[9] = positionData.collateral;
      $[10] = positionData.market;
      $[11] = proposedCollateral;
      $[12] = protectedLltv;
      $[13] = t7;
      $[14] = t8;
      $[15] = t9;
    } else {
      t8 = $[14];
      t9 = $[15];
    }
    let t10;
    if ($[16] !== t8 || $[17] !== t9) {
      t10 = {
        maxBorrow: t8,
        maxBorrowCeiling: t9
      };
      $[16] = t8;
      $[17] = t9;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    t6 = t10;
  }
  const {
    maxBorrow,
    maxBorrowCeiling
  } = t6;
  const t7 = enabled && (isProtectedLltvPending || account.isConnected && isLoading);
  let t8;
  if ($[19] !== maxBorrow || $[20] !== maxBorrowCeiling || $[21] !== t7) {
    t8 = {
      maxBorrow,
      maxBorrowCeiling,
      isMaxLoading: t7
    };
    $[19] = maxBorrow;
    $[20] = maxBorrowCeiling;
    $[21] = t7;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
}